import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { signUp } from 'aws-amplify/auth';
import { LoginProvider, Platform, UserState } from '~/types';
import { PAGE_ROUTE } from '~/constants';
import AuthService from '~/services/AuthService';
import { useAuthStore, useSignUpStepStore } from '~/store';
import useToast from '~/store/useToast';
import InnerLayout from '../Layout/InnerLayout';
import Birth from './Birth';
import Gender from './Gender';
import UserName from './UserName';

interface ProfileProps {
  onNextStep: () => void;
}

export default function Profile({ onNextStep }: ProfileProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const {
    type,
    email,
    password,
    agreement: { required, required2, marketing },
  } = useSignUpStepStore((state) => ({
    type: state.type,
    email: state.verifyEmail.email,
    password: state.createPassword.password,
    agreement: state.agreement,
  }));
  const { name, birth, gender, language } = useSignUpStepStore((state) => ({
    name: state.profile.name,
    birth: state.profile.birth,
    gender: state.profile.gender,
    language: state.language.language,
  }));

  const loginMethod = useAuthStore((state) => state.loginMethod);
  const setAuthLoading = useAuthStore((state) => state.setAuthLoading);
  const isOAuth = type === 'sns';

  const resetAll = useSignUpStepStore((state) => state.resetAll);
  const setSubProgress = useSignUpStepStore((state) => state.setSubProgress);

  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [birthValid, setBirthValid] = useState(true);
  const [usernameValid, setUsernameValid] = useState(true);

  const isProfileValid = usernameValid && birthValid;

  useEffect(() => {
    const currentSession = async () => {
      try {
        const sessionEmail = await AuthService.getSessionEmail();
        if (sessionEmail) {
          setSessionEmail(sessionEmail);
        }
      } catch (error) {
        console.error(error);
      }
    };
    currentSession();
  }, []);

  useEffect(() => {
    const process = isProfileValid ? 1 : 0.5;
    setSubProgress(process);
  }, [isProfileValid, setSubProgress]);

  const handleContinueClick = async () => {
    try {
      setAuthLoading(true);
      if (isOAuth && !sessionEmail) {
        showToast('error', 'Please login again.');
        navigate(PAGE_ROUTE.SIGNIN);
        return;
      }

      const platformMap: Record<LoginProvider, Platform> = {
        Google: 'google',
        Apple: 'apple',
        email: 'cognito',
      };
      const newUser: UserState = {
        platform:
          platformMap[isOAuth ? (loginMethod as LoginProvider) : 'email'],
        email: isOAuth ? sessionEmail ?? email : email,
        name,
        nickname: name,
        language,
        gender: gender || null,
        birth_date: birth,
        privacy_agree: required,
        avatar_agree: required2,
        marketing_agree: marketing,
        timezone_iana: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      if (isOAuth) {
        const isSignUpSuccess = await AuthService.saveUserInfo(newUser);
        if (isSignUpSuccess) {
          resetAll();
          showToast(
            'success',
            `You have successfully logged in with ${loginMethod}.`,
          );
          navigate(PAGE_ROUTE.MAIN);
        }
      } else {
        const handleSignUpInfo = {
          username: email,
          password,
          options: {
            userAttributes: { email },
            autoSignIn: false,
          },
        };
        const { nextStep } = await signUp(handleSignUpInfo);
        if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          const isSaveUserInfoSuccess = await AuthService.saveUserInfo(newUser);
          if (isSaveUserInfoSuccess) {
            onNextStep();
          }
        }
      }
    } catch (error: any) {
      Sentry.captureException(error);

      if (error.name === 'UsernameExistsException') {
        showToast(
          'error',
          'An account with the given email already exists. Please try to login.',
        );
      } else {
        showToast('error', error.message);
      }
      navigate(PAGE_ROUTE.SIGNIN);
    } finally {
      setAuthLoading(false);
    }
  };

  const buttonProps = {
    title: 'Continue',
    disabled: !isProfileValid,
    onClick: handleContinueClick,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <div>
        <UserName setUsernameValid={setUsernameValid} />
        <Birth setBirthValid={setBirthValid} />
        <Gender />
        {/* <Language /> */}
      </div>
    </InnerLayout>
  );
}
